import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box
} from '@mui/material';
import InputIcon from '@mui/icons-material/Input';
import MapIcon from '@mui/icons-material/Map';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import InfoIcon from '@mui/icons-material/Info';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';

// Define theme colors for consistency (can be imported if shared)
const THEME = {
  colors: {
    primary: '#FF6B6B',  // Coral pink
    secondary: '#4ECDC4', // Mint
    text: '#363636',    // Soft black
    background: '#FFFFFF',
    accent3: '#FFEEAD' // Soft peach for list item hover
  }
};

interface WelcomePopupProps {
  open: boolean;
  onClose: () => void;
}

const WelcomePopup: React.FC<WelcomePopupProps> = ({ open, onClose }) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3, // Match Card border radius
          boxShadow: '0 12px 48px rgba(0, 0, 0, 0.15)', // Match Card shadow
          bgcolor: THEME.colors.background
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 2, pt: 3, borderBottom: `1px solid ${THEME.colors.primary}20` }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 700, color: THEME.colors.text }}>
          Welcome to WTG!
        </Typography>
        <Typography variant="subtitle1" sx={{ color: THEME.colors.text, opacity: 0.8 }}>
          Your Personal Walking Tour Generator
        </Typography>
      </DialogTitle>
      <DialogContent 
        sx={{ 
          pt: 4,
          pb: 0.25,
          px: { xs: 2, sm: 3 }
        }}
      >
        <List sx={{ p: 0 }}>
          {[
            {
              icon: <InfoIcon sx={{ color: THEME.colors.primary }} />,
              primary: "Enter Your Destination",
              secondary: "Enter the city or area you want to explore, the duration and your interests."
            },
            {
              icon: <MapIcon sx={{ color: THEME.colors.primary }} />,
              primary: "Get Your Custom Tour",
              secondary: "WTG! will generate a personalized itinerary with information and stories for each stop. This may take a minute or two."
            },
            {
              icon: <DirectionsWalkIcon sx={{ color: THEME.colors.primary }} />,
              primary: "View the Route",
              secondary: "See your tour laid out on an interactive map with walking directions."
            },
            {
              icon: <VolumeUpIcon sx={{ color: THEME.colors.primary }} />,
              primary: "Listen Along",
              secondary: "Enable audio narration for a guided experience as you walk."
            },
            {
                icon: <QuestionMarkIcon sx={{ color: THEME.colors.primary }} />,
                primary: "Ask Questions",
                secondary: "Ask questions to your virtual guide and get answers on the go."
            },
            {
                icon: <BookmarkBorderIcon sx={{ color: THEME.colors.primary }} />,
                primary: "Save Tours for Later",
                secondary: "Sign up for a free account to save your walking tours and revisit them anytime."
            }
          ].map((item, index) => (
            <ListItem 
              key={index}
              sx={{
                mb: 1,
                borderRadius: 2,
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: `${THEME.colors.accent3}80`
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.primary} 
                secondary={item.secondary} 
                primaryTypographyProps={{ fontWeight: 600, color: THEME.colors.text }}
                secondaryTypographyProps={{ color: THEME.colors.text, sx: { opacity: 0.7 } }}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', p: 3, borderTop: `1px solid ${THEME.colors.primary}20` }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          size="large"
          sx={{
            py: 1.5,
            px: 5,
            borderRadius: 2,
            backgroundColor: THEME.colors.secondary,
            transition: 'all 0.2s ease',
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#45b8b0',
              boxShadow: `0 4px 12px ${THEME.colors.secondary}33`
            }
          }}
        >
          Let's Walk!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WelcomePopup; 